import { useTranslation } from "react-i18next";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import type React from "react";

const steps = [
  {
    number: "①",
    icon: "ri-file-text-line",
    titleKey: "flow_step1_title",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    borderColor: "border-[#0ABAB5]",
    contentKeys: ["flow_step1_c1", "flow_step1_c2", "flow_step1_c3"],
    noteKey: null,
  },
  {
    number: "②",
    icon: "ri-bank-line",
    titleKey: "flow_step2_title",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    borderColor: "border-[#0ABAB5]",
    contentKeys: ["flow_step2_c1", "flow_step2_c2"],
    noteKey: null,
  },
  {
    number: "③",
    icon: "ri-mail-send-line",
    titleKey: "flow_step3_title",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    borderColor: "border-[#0ABAB5]",
    contentKeys: ["flow_step3_c1", "flow_step3_c2"],
    noteKey: "flow_step3_note",
  },
  {
    number: "④",
    icon: "ri-quill-pen-line",
    titleKey: "flow_step4_title",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    borderColor: "border-[#0ABAB5]",
    contentKeys: ["flow_step4_c1", "flow_step4_c2"],
    noteKey: null,
  },
  {
    number: "⑤",
    icon: "ri-mail-line",
    titleKey: "flow_step5_title",
    color: "bg-[#e8f9f8]",
    iconColor: "text-[#0ABAB5]",
    borderColor: "border-[#0ABAB5]",
    contentKeys: ["flow_step5_c1", "flow_step5_c2"],
    noteKey: "flow_step5_note",
  },
];

export default function FlowSection() {
  const { t } = useTranslation();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { setRef, visibleItems } = useScrollAnimationMultiple(steps.length, { threshold: 0.08 });
  const { ref: bannerRef, isVisible: bannerVisible } = useScrollAnimation();

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="flow" className="py-16 sm:py-24 bg-[#e8f9f8]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-5"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {t("flow_heading")}
          </h2>
        </div>

        {/* Steps */}
        <div className="relative">
          <div className="absolute left-7 top-0 bottom-0 w-px bg-gray-100 hidden md:block" />
          <div className="space-y-5">
            {steps.map((step, i) => (
              <div
                key={i}
                ref={setRef(i) as unknown as React.RefObject<HTMLDivElement>}
                className={`relative flex gap-4 md:gap-8 transition-all duration-700 ease-out ${visibleItems[i] ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}
                style={{ transitionDelay: visibleItems[i] ? `${i * 80}ms` : "0ms" }}
              >
                {/* Circle */}
                <div className="flex-shrink-0 relative z-10">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-full ${step.color} border-2 ${step.borderColor}`}>
                    <i className={`${step.icon} ${step.iconColor} text-lg md:text-xl`} />
                  </div>
                </div>

                {/* Card */}
                <div className="flex-1 bg-white/80 border border-[#0ABAB5]/10 rounded-2xl px-4 md:px-7 py-5 md:py-6 mb-2 min-w-0">
                  <div className="flex items-center gap-2 md:gap-3 mb-3">
                    <span className="text-xs font-bold text-gray-400 tracking-widest flex-shrink-0">{step.number}</span>
                    <h3
                      className="text-base md:text-lg font-bold text-gray-800 leading-snug"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {t(step.titleKey)}
                    </h3>
                  </div>

                  <ul className="space-y-2">
                    {step.contentKeys.map((key) => (
                      <li key={key} className="flex items-start gap-2 text-xs md:text-sm text-gray-600 leading-relaxed">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-blank-circle-fill text-[#0ABAB5] text-[6px]" />
                        </div>
                        {t(key)}
                      </li>
                    ))}
                  </ul>

                  {step.noteKey && (
                    <div className="mt-4 bg-[#e8f9f8] border border-[#0ABAB5]/30 rounded-lg px-3 md:px-4 py-3">
                      <p className="text-xs text-[#0ABAB5] leading-relaxed">
                        <i className="ri-information-line mr-1.5" />
                        {t(step.noteKey)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom banner */}
        <div
          ref={bannerRef as React.RefObject<HTMLDivElement>}
          className={`mt-14 bg-[#e8f9f8] rounded-2xl border border-[#0ABAB5]/20 px-6 md:px-8 py-8 text-center transition-all duration-700 ease-out ${bannerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: bannerVisible ? "100ms" : "0ms" }}
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white mx-auto mb-4">
            <i className="ri-global-line text-[#0ABAB5] text-xl" />
          </div>
          <h4
            className="text-xl font-bold text-gray-800 mb-2"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {t("flow_banner_title")}
          </h4>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            {t("flow_banner_desc").split("\n").map((line, i, arr) => (
              <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
            ))}
          </p>
          <button
            onClick={() => handleNav("#contact")}
            className="inline-flex items-center gap-2 bg-[#0ABAB5] text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-[#089e99] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("flow_banner_cta")}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
